import React, { useEffect, useState } from 'react';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import { PurchaseDocumentDataForm, PurchaseDocumentServiceManifestDataForm } from '../../../../app/models/PurchaseDocument'; 
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import { Society } from '../../../../app/models/Society';
import LazyLoading from '../../../../components/LazyLoading';
import { momentParseDate } from '../../../../helpers';
import InputButtonFile from '../../../../components/inputs/InputButtonFile';
import { Provider } from '../../../../app/models/Provider';


interface Props {
    purchaseDocument: PurchaseDocumentDataForm;
    societies: Society[];
    providers: Provider[];
    disabled?: boolean;
    setPurchaseDocument: (purchaseDocument: PurchaseDocumentDataForm) => void;
    availableStatuses: PurchaseDocumentServiceManifestDataForm[];
    errorFields?: any;
    loadingProvider?: boolean;
    loadingServiceManifestStatus?: boolean;
}

const PurchaseDocumentForm = ({ 
      purchaseDocument,
      setPurchaseDocument,
      availableStatuses, 
      societies,
      providers,
      disabled = false,
      loadingProvider,
      loadingServiceManifestStatus,
      errorFields 
    }: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors, errors } = useHandleErrorFields();

    const [addedServiceManifests, setAddedServiceManifests] = useState<PurchaseDocumentServiceManifestDataForm[]>([]);
    const [selectedServiceManifest, setSelectedServiceManifest] = useState<PurchaseDocumentServiceManifestDataForm | null>(null);
    useEffect(() => {
  console.log({
    selectedServiceManifest,
    availableStatuses,
  });
}, [selectedServiceManifest, availableStatuses]);

    const [totalAmount, setTotalAmount] = useState<number>(0);

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields, setErrors]);

    const parseCurrencyToNumber = (currency: string | number) => {
        if (typeof currency === 'string') {
            return parseFloat(currency.replace(/\./g, '').replace(/,/g, '').replace(/[^0-9.-]+/g, ''));
        }
        return currency;
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const mappedStatuses = availableStatuses.map((status) => ({
        id: status.id,
        name: `#${status.id} - ${status.provider_name} - Total: $${new Intl.NumberFormat('es-CL').format(status.total)}`,
        total: status.total,
        provider_name: status.provider_name,
        total_tax: status.total_tax,
        total_neto: status.total_neto,
        total_unit: status.total_neto,
    }));

    const handleSelectChange = (name: string, value: any) => {
        const selected = mappedStatuses.find((status) => status.id === value.value);
        setSelectedServiceManifest(selected || null);
    };

    const handleChangeSelectTwoProvider = (name: string, value: number | string | null) => {
        setPurchaseDocument({
            ...purchaseDocument,
            [name]: value,
            provider_email: providers.find((provider) => provider.id == value)?.email || ''
        });
    };

    const handleChangeSelectTwo = (name: string, value: number | string | null) => {
        if(name === 'society_id'){
            setErrors(
                {
                    ...errors,
                    provider_id:''
                }
            )
        }
        setPurchaseDocument({ ...purchaseDocument, [name]: value });
    };

    const handleChange = (
        e:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;

        setPurchaseDocument({ ...purchaseDocument, [name]: value });
    };


    const addServiceManifest = () => {
        if (selectedServiceManifest && !addedServiceManifests.some(p => p.id === selectedServiceManifest.id)) {
            setAddedServiceManifests([...addedServiceManifests, selectedServiceManifest]);
        }
    };

    const removeServiceManifest = (id: number) => {
        const updatedServiceManifests = addedServiceManifests.filter(payment => payment.id !== id);
        setAddedServiceManifests(updatedServiceManifests);
    };


    const handleChangeDocumentFile = (e: FileList | null, type: string) => {
        if (e) {
            setPurchaseDocument({ ...purchaseDocument, ['file']: e[0] });
        }
    };

    const handleRemoveFile = (name: string) => {
        setPurchaseDocument({ 
            ...purchaseDocument, 
            [name]: undefined 
        });
        const fileInput = document.getElementById(name) as HTMLInputElement | null;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    useEffect(() => {
        const total = addedServiceManifests.reduce((sum, payment) => {
            const paymentTotal = typeof payment.total === 'string' 
                ? Number(payment.total)
                : payment.total;
            return sum + paymentTotal;
        }, 0);

        const paymentStatusesIds = addedServiceManifests.map(payment => payment.id);

        setTotalAmount(total);

        setPurchaseDocument({
            ...purchaseDocument,
            total_amount: total,
            service_manifest_id: paymentStatusesIds
        });
    }, [addedServiceManifests, setPurchaseDocument]);

    

    return (
        <div className="purchase-document-form">

            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Sociedad</label>
                <div className="col-md-9">
                    <SelectTwo
                        name="society_id"
                        id="society_id"
                        inputValue={purchaseDocument.society_id}
                        options={SelectTwoMapperOptions(societies)}
                        hasError={fieldHasError('society_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('society_id', value.value)}
                        placeholder={'Seleccione una sociedad'}
                        onFocus={() => onFocusRemove('society_id')}
                        disabled={disabled}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('society_id')}
                    </div>
                </div>
            </div>

                    { loadingProvider ?
                    <LazyLoading height={300} />
                    :
                    <div className="mb-2 row">
                        <label className="col-md-3 col-form-label">Proveedor</label>
                        <div className="col-md-9">
                            <SelectTwo
                                name="provider_id"
                                id="provider_id"
                                inputValue={purchaseDocument.provider_id}
                                options={SelectTwoMapperOptions(providers)}
                                hasError={fieldHasError('provider_id') !== ''}
                                onChange={(value: any) =>
                                    handleChangeSelectTwoProvider('provider_id', value.value)
                                }
                                placeholder={'Seleccione un proveedor'}
                                onFocus={() => onFocusRemove('provider_id')}
                                disabled={disabled}
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('provider_id')}
                            </div>
                        </div>
                    </div>
                    }

                    {!disabled && ( 
                    <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Folio Factura</label>
                    <div className="col-md-9">
                        <input
                            className={`form-control ${fieldHasError('folio')}`}
                            name="folio"
                            id="folio"
                            type="text"
                            value={purchaseDocument.folio}
                            onChange={handleChange}
                            placeholder="Ingrese el folio de la factura"
                            onFocus={() => onFocusRemove('folio')}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('folio')}
                        </div>
                    </div>
                    </div>

                    )}
                    {!disabled && ( 

                    <div className="mb-2 row">
                        <label className="col-md-3 col-form-label">Fecha de emisión</label>
                        <div className="col-md-9">
                            <input
                                type="date"
                                className="form-control"
                                id="issue_date"
                                name="issue_date"
                                value={purchaseDocument.issue_date}
                                onChange={handleChange}
                                onFocus={() => onFocusRemove('issue_date')}
                                // disabled={disabled}
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('issue_date')}
                            </div>
                        </div>
                    </div>
                    )}

                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Factura de Compra</label>
                    <div className="col-md-9">

                        <InputButtonFile
                            name={'file'}
                            objectFile={purchaseDocument.file}
                            handleChangeDocumentFile={handleChangeDocumentFile}
                            handleRemoveFile={(value: any) => handleRemoveFile('file')}
                            onFocus={() => onFocusRemove('file')}
                            // disabled={disabled}
                        />

                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('file')}
                        </div>
                    </div>
                </div>
            {
                loadingServiceManifestStatus ?
                <LazyLoading height={300} />
                :
                <>
                    <label className="col-md-6 col-form-label">Documento de Factura</label>
                    <div className="d-flex mb-3 align-items-stretch">
                        <div className="flex-grow-1 me-2">
                            <SelectTwo
                                name="services_manifest_id"
                                id="services_manifest_id"
                                inputValue={selectedServiceManifest ? selectedServiceManifest.id : null}
                                hasError={fieldHasError('service_manifest_id') !== ''}
                                options={SelectTwoMapperOptions(mappedStatuses, 'id', ['name'])}
                                onChange={(value: any) => handleSelectChange('services_manifest_id', value)}
                                onFocus={() => onFocusRemove('service_manifest_id')}
                                placeholder="Seleccionar Documento"
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('service_manifest_id')}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={addServiceManifest}
                            disabled={!selectedServiceManifest}
                        >
                            Agregar Documento
                        </button>
                    </div>
                </>
            }
            
            <div className="table-form">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Nro Documento</th>
                            {/* <th>Providere</th> */}
                            <th>Neto</th>
                            <th>Impuesto</th>
                            <th>Total</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {addedServiceManifests.map((payment) => (
                            <tr key={payment.id}>
                                <td data-label="Nro">#{payment.id}</td>
                                {/* <td data-label="Providere">{payment.provider_name}</td> */}
                                <td data-label="Neto">{formatCurrency(Number(payment.total_neto))}</td>
                                <td data-label="Impuesto">{formatCurrency(Number(payment.total_tax))}</td>
                                <td data-label="Total">{formatCurrency(payment.total)}</td>
                                <td data-label="Acciones">
                                    <ButtonTableAction
                                        callbackFunction={() => removeServiceManifest(payment.id ?? -1)}
                                        classIcon={'mdi mdi-delete'}
                                        colorIcon={'text-danger'}
                                        errorMessage={'No se puede eliminar este registro.'}
                                        title={'Eliminar'}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="total mt-3 d-flex justify-content-end">
                <span className="me-2">Total:</span>
                <span>{formatCurrency(totalAmount)}</span>
            </div>
        </div>
    );
};

export default PurchaseDocumentForm;
